/**
 * Shared Paper / Ink / Stone shell for ?test= continuity prototypes.
 * Tokens from design/landing-prototype/index-v2.html (maater-design-ui).
 * Also remaps Tailwind semantic HSL vars so bg-background / text-primary / etc. track the theme.
 */

import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type MaaterProtoThemeId = 'paper' | 'ink' | 'stone'

/** OKLCH product tokens (explicit style={{ color: 'var(--rust)' }} etc.) */
export const MAATER_PROTO_OKLCH: Record<MaaterProtoThemeId, Record<string, string>> = {
  paper: {
    '--paper': 'oklch(97% 0.012 75)',
    '--paper-raised': 'oklch(93.5% 0.02 72)',
    '--paper-deep': 'oklch(90% 0.025 68)',
    '--ink': 'oklch(24% 0.03 50)',
    '--ink-soft': 'oklch(42% 0.025 52)',
    '--ink-faint': 'oklch(58% 0.02 55)',
    '--line': 'oklch(85% 0.02 68)',
    '--rust': 'oklch(56% 0.17 35)',
    '--rust-pale': 'oklch(93% 0.05 40)',
    '--cream-card': 'oklch(98.5% 0.008 80)',
  },
  ink: {
    '--paper': 'oklch(18% 0.02 50)',
    '--paper-raised': 'oklch(22% 0.022 48)',
    '--paper-deep': 'oklch(26% 0.025 45)',
    '--ink': 'oklch(94% 0.015 75)',
    '--ink-soft': 'oklch(78% 0.02 70)',
    '--ink-faint': 'oklch(62% 0.02 65)',
    '--line': 'oklch(32% 0.02 50)',
    '--rust': 'oklch(68% 0.16 40)',
    '--rust-pale': 'oklch(28% 0.06 40)',
    '--cream-card': 'oklch(20% 0.018 50)',
  },
  stone: {
    '--paper': 'oklch(88% 0.01 95)',
    '--paper-raised': 'oklch(84% 0.014 90)',
    '--paper-deep': 'oklch(80% 0.018 85)',
    '--ink': 'oklch(28% 0.02 60)',
    '--ink-soft': 'oklch(40% 0.02 55)',
    '--ink-faint': 'oklch(52% 0.018 55)',
    '--line': 'oklch(74% 0.015 85)',
    '--rust': 'oklch(50% 0.14 35)',
    '--rust-pale': 'oklch(86% 0.04 50)',
    '--cream-card': 'oklch(91% 0.01 95)',
  },
}

/**
 * HSL channel triples for shadcn tokens (used as hsl(var(--background))).
 * Approximates Paper / Ink / Stone so existing Tailwind utility classes follow the proto theme.
 */
export const MAATER_PROTO_SEMANTIC_HSL: Record<MaaterProtoThemeId, Record<string, string>> = {
  paper: {
    '--background': '40 33% 97%',
    '--foreground': '30 20% 18%',
    '--card': '40 40% 98.5%',
    '--card-foreground': '30 20% 18%',
    '--popover': '40 40% 98.5%',
    '--popover-foreground': '30 20% 18%',
    '--primary': '20 65% 40%',
    '--primary-foreground': '40 30% 98%',
    '--secondary': '35 22% 92%',
    '--secondary-foreground': '30 20% 18%',
    '--muted': '35 25% 92%',
    '--muted-foreground': '30 12% 42%',
    '--accent': '30 40% 93%',
    '--accent-foreground': '20 65% 32%',
    '--destructive': '0 72% 48%',
    '--destructive-foreground': '0 0% 98%',
    '--border': '35 18% 85%',
    '--input': '35 18% 85%',
    '--ring': '20 65% 40%',
  },
  ink: {
    '--background': '30 15% 12%',
    '--foreground': '40 20% 94%',
    '--card': '30 14% 16%',
    '--card-foreground': '40 20% 94%',
    '--popover': '30 14% 16%',
    '--popover-foreground': '40 20% 94%',
    '--primary': '25 55% 58%',
    '--primary-foreground': '30 15% 10%',
    '--secondary': '30 12% 20%',
    '--secondary-foreground': '40 20% 94%',
    '--muted': '30 12% 18%',
    '--muted-foreground': '35 12% 70%',
    '--accent': '25 30% 22%',
    '--accent-foreground': '25 55% 70%',
    '--destructive': '0 62% 50%',
    '--destructive-foreground': '0 0% 98%',
    '--border': '30 10% 25%',
    '--input': '30 10% 25%',
    '--ring': '25 55% 58%',
  },
  stone: {
    '--background': '40 8% 88%',
    '--foreground': '30 15% 22%',
    '--card': '40 10% 91%',
    '--card-foreground': '30 15% 22%',
    '--popover': '40 10% 91%',
    '--popover-foreground': '30 15% 22%',
    '--primary': '22 55% 38%',
    '--primary-foreground': '40 20% 97%',
    '--secondary': '40 10% 84%',
    '--secondary-foreground': '30 15% 22%',
    '--muted': '40 10% 84%',
    '--muted-foreground': '30 10% 40%',
    '--accent': '30 25% 86%',
    '--accent-foreground': '22 55% 32%',
    '--destructive': '0 65% 42%',
    '--destructive-foreground': '0 0% 98%',
    '--border': '40 8% 78%',
    '--input': '40 8% 78%',
    '--ring': '22 55% 38%',
  },
}

const FONT_LINK_ID = 'maater-prototype-fonts'
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Karla:wght@400;500;600;700&display=swap'

export function ensureMaaterProtoFonts() {
  if (typeof document === 'undefined') return
  if (document.getElementById(FONT_LINK_ID)) return
  const link = document.createElement('link')
  link.id = FONT_LINK_ID
  link.rel = 'stylesheet'
  link.href = FONT_HREF
  document.head.appendChild(link)
}

export function maaterProtoShellStyle(theme: MaaterProtoThemeId): CSSProperties {
  return {
    ...MAATER_PROTO_OKLCH[theme],
    ...MAATER_PROTO_SEMANTIC_HSL[theme],
    background: 'var(--paper)',
    color: 'var(--ink)',
    fontFamily: 'Karla, ui-sans-serif, system-ui, sans-serif',
  } as CSSProperties
}

export function MaaterProtoShell({
  theme,
  children,
  className,
  testId,
}: {
  theme: MaaterProtoThemeId
  children: ReactNode
  className?: string
  testId?: string
}) {
  useEffect(() => {
    ensureMaaterProtoFonts()
  }, [])

  return (
    <div
      data-maater-proto={theme}
      data-testid={testId}
      className={cn('min-h-screen antialiased', className)}
      style={maaterProtoShellStyle(theme)}
    >
      {children}
    </div>
  )
}

/** Compact theme / surface segment - rust border when active (MaaterSegmentControl). */
export function MaaterProtoSegmentTrack({
  options,
  value,
  onChange,
  className,
}: {
  options: { id: string; label: ReactNode }[]
  value: string
  onChange: (id: string) => void
  className?: string
}) {
  return (
    <div
      className={cn('inline-flex flex-wrap gap-1 rounded-[10px] p-[3px]', className)}
      style={{ background: 'var(--paper-deep)' }}
    >
      {options.map((opt) => {
        const active = opt.id === value
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
            style={
              active
                ? {
                    background: 'var(--cream-card)',
                    border: '1.5px solid var(--rust)',
                    color: 'var(--ink)',
                  }
                : {
                    background: 'transparent',
                    border: '1.5px solid transparent',
                    color: 'var(--ink-soft)',
                  }
            }
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

export const MAATER_PROTO_THEME_OPTIONS: { id: MaaterProtoThemeId; label: string }[] = [
  { id: 'paper', label: 'Paper' },
  { id: 'ink', label: 'Ink' },
  { id: 'stone', label: 'Stone' },
]

/** Production ThemeProvider preference - for `/?test=maater-ui` (webapp contract SoT). */
export type MaaterAppThemeId = 'light' | 'dark' | 'system'

export const MAATER_APP_THEME_OPTIONS: { id: MaaterAppThemeId; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'System' },
]

/**
 * Bridge proto OKLCH var names (--paper, --ink, --rust, ...) onto live shadcn tokens.
 * Use on `/?test=maater-ui` so Proto* chrome follows ThemeProvider light/dark.
 * Does not override --background / --foreground (ThemeProvider owns those).
 */
export function maaterProtoAppSkinStyle(): CSSProperties {
  return {
    '--paper': 'hsl(var(--background))',
    '--paper-raised': 'hsl(var(--card))',
    '--paper-deep': 'hsl(var(--muted))',
    '--ink': 'hsl(var(--foreground))',
    '--ink-soft': 'hsl(var(--muted-foreground))',
    '--ink-faint': 'hsl(var(--muted-foreground))',
    '--line': 'hsl(var(--border))',
    '--rust': 'hsl(var(--primary))',
    '--rust-pale': 'hsl(var(--primary) / 0.12)',
    '--cream-card': 'hsl(var(--accent))',
  } as CSSProperties
}
